import { defineMessages } from '../define';

/**
 * API key management.
 *
 * Scope identifiers (`messages:read` and friends) are wire values and stay in
 * English everywhere; only the sentence explaining what each one permits is
 * translated.
 */
export const apiKeys = defineMessages({
  en: {
    'apiKeys.title': 'API keys',
    'apiKeys.description': 'Machine credentials for calling Threadline from your own code.',
    'apiKeys.create': 'Create key',
    /**
     * `{link}` is replaced by the /api/docs anchor at render time, so the whole
     * sentence stays one string and Thai can place the link where it reads
     * naturally.
     */
    'apiKeys.intro':
      'A key authenticates as you and is limited to the scopes you grant it, so give each integration its own and revoke the one that leaks rather than all of them. The full endpoint reference, with request and response shapes, is at {link}.',

    'apiKeys.column.name': 'Name',
    'apiKeys.column.prefix': 'Prefix',
    'apiKeys.column.scopes': 'Scopes',
    'apiKeys.column.lastUsed': 'Last used',
    'apiKeys.column.expires': 'Expires',
    'apiKeys.column.status': 'Status',

    'apiKeys.noExpiry': 'No expiry',
    'apiKeys.status.revoked': 'Revoked',
    'apiKeys.status.expired': 'Expired',

    'apiKeys.revoke': 'Revoke',
    'apiKeys.revoke.title': 'Revoke {name}?',
    'apiKeys.revoke.description':
      'Any integration using this key stops working immediately. The row is kept so audit entries still resolve.',
    'apiKeys.revoke.confirm': 'Revoke key',
    'apiKeys.toast.revoked': 'Key revoked',

    'apiKeys.empty.title': 'No API keys',
    'apiKeys.empty.description':
      'Create one to call the API from a script, an automation tool, or another service on your network.',

    'apiKeys.dialog.create': 'Create API key',
    'apiKeys.dialog.createDescription': 'Grant the narrowest set of scopes the integration needs.',

    'apiKeys.field.name': 'Name',
    'apiKeys.field.namePlaceholder': 'Zapier integration',
    'apiKeys.field.nameHint':
      'Whatever will tell you which system to go and fix when this key misbehaves.',
    'apiKeys.field.expires': 'Expires',
    'apiKeys.field.expiresHint':
      'Optional. An expiry is the cheapest way to make sure a forgotten key stops working.',

    'apiKeys.scopes.title': 'Scopes',
    'apiKeys.scopes.selectAll': 'Select all',
    'apiKeys.scopes.clear': 'Clear',

    'apiKeys.group.sessions': 'Sessions',
    'apiKeys.group.messages': 'Messages',
    'apiKeys.group.chats': 'Chats and contacts',
    'apiKeys.group.webhooks': 'Webhooks',
    'apiKeys.group.other': 'Other',

    'apiKeys.scope.sessionsRead': 'List sessions and read their status',
    'apiKeys.scope.sessionsWrite': 'Start, stop and delete sessions',
    'apiKeys.scope.messagesRead': 'Search and read stored messages',
    'apiKeys.scope.messagesSend': 'Send messages through a connected session',
    'apiKeys.scope.chatsRead': 'List chats and their metadata',
    'apiKeys.scope.contactsRead': 'List contacts',
    'apiKeys.scope.webhooksRead': 'List endpoints and their deliveries',
    'apiKeys.scope.webhooksWrite': 'Create, edit, test and delete endpoints',
    'apiKeys.scope.dashboardRead': 'Read the overview counters',
    'apiKeys.scope.aiInvoke': 'Run AI tasks against stored messages',

    'apiKeys.created.title': 'Your new API key',
    'apiKeys.created.description': 'Key for {name}.',
    'apiKeys.created.warning':
      'Copy this now. Only a SHA-256 digest of it is stored, so it cannot be shown again or recovered — a lost key has to be revoked and replaced.',
    'apiKeys.created.copy': 'Copy API key',
    'apiKeys.created.tryIt': 'Try it',
    'apiKeys.created.done': 'I have copied it',

    'apiKeys.requestFailed':
      'The request could not be completed. Check that the gateway is reachable.',
  },
  th: {
    'apiKeys.title': 'API key',
    'apiKeys.description': 'ข้อมูลรับรองสำหรับเรียกใช้ Threadline จากโค้ดของคุณเอง',
    'apiKeys.create': 'สร้างคีย์',
    'apiKeys.intro':
      'คีย์จะยืนยันตัวตนในนามของคุณ และใช้ได้เฉพาะขอบเขตสิทธิ์ที่คุณกำหนดให้ ควรออกคีย์แยกให้แต่ละการเชื่อมต่อ เพื่อที่เมื่อคีย์ใดรั่วไหลจะเพิกถอนเฉพาะคีย์นั้นได้โดยไม่กระทบตัวอื่น เอกสาร endpoint ทั้งหมดพร้อมรูปแบบ request และ response อยู่ที่ {link}',

    'apiKeys.column.name': 'ชื่อ',
    'apiKeys.column.prefix': 'คำนำหน้าคีย์',
    'apiKeys.column.scopes': 'ขอบเขตสิทธิ์',
    'apiKeys.column.lastUsed': 'ใช้งานล่าสุด',
    'apiKeys.column.expires': 'หมดอายุ',
    'apiKeys.column.status': 'สถานะ',

    'apiKeys.noExpiry': 'ไม่มีวันหมดอายุ',
    'apiKeys.status.revoked': 'เพิกถอนแล้ว',
    'apiKeys.status.expired': 'หมดอายุแล้ว',

    'apiKeys.revoke': 'เพิกถอน',
    'apiKeys.revoke.title': 'เพิกถอน {name} หรือไม่',
    'apiKeys.revoke.description':
      'ระบบที่ใช้คีย์นี้อยู่จะหยุดทำงานทันที แต่ข้อมูลคีย์จะยังเก็บไว้เพื่อให้บันทึกตรวจสอบอ้างอิงได้',
    'apiKeys.revoke.confirm': 'เพิกถอนคีย์',
    'apiKeys.toast.revoked': 'เพิกถอนคีย์แล้ว',

    'apiKeys.empty.title': 'ยังไม่มี API key',
    'apiKeys.empty.description':
      'สร้างคีย์เพื่อเรียกใช้ API จากสคริปต์ เครื่องมืออัตโนมัติ หรือบริการอื่นในเครือข่ายของคุณ',

    'apiKeys.dialog.create': 'สร้าง API key',
    'apiKeys.dialog.createDescription': 'ให้สิทธิ์เท่าที่การเชื่อมต่อนั้นจำเป็นต้องใช้เท่านั้น',

    'apiKeys.field.name': 'ชื่อ',
    'apiKeys.field.namePlaceholder': 'เชื่อมต่อกับ Zapier',
    'apiKeys.field.nameHint': 'ตั้งชื่อให้รู้ได้ทันทีว่าต้องไปแก้ที่ระบบไหนเมื่อคีย์นี้มีปัญหา',
    'apiKeys.field.expires': 'วันหมดอายุ',
    'apiKeys.field.expiresHint':
      'ไม่บังคับ การตั้งวันหมดอายุเป็นวิธีที่ง่ายที่สุดที่จะทำให้คีย์ที่ลืมไปแล้วหยุดทำงานเอง',

    'apiKeys.scopes.title': 'ขอบเขตสิทธิ์',
    'apiKeys.scopes.selectAll': 'เลือกทั้งหมด',
    'apiKeys.scopes.clear': 'ล้าง',

    'apiKeys.group.sessions': 'เซสชัน',
    'apiKeys.group.messages': 'ข้อความ',
    'apiKeys.group.chats': 'แชทและรายชื่อ',
    'apiKeys.group.webhooks': 'Webhook',
    'apiKeys.group.other': 'อื่น ๆ',

    'apiKeys.scope.sessionsRead': 'ดูรายการเซสชันและสถานะ',
    'apiKeys.scope.sessionsWrite': 'เริ่ม หยุด และลบเซสชัน',
    'apiKeys.scope.messagesRead': 'ค้นหาและอ่านข้อความที่เก็บไว้',
    'apiKeys.scope.messagesSend': 'ส่งข้อความผ่านเซสชันที่เชื่อมต่ออยู่',
    'apiKeys.scope.chatsRead': 'ดูรายการแชทและข้อมูลประกอบ',
    'apiKeys.scope.contactsRead': 'ดูรายชื่อผู้ติดต่อ',
    'apiKeys.scope.webhooksRead': 'ดู endpoint และประวัติการส่ง',
    'apiKeys.scope.webhooksWrite': 'สร้าง แก้ไข ทดสอบ และลบ endpoint',
    'apiKeys.scope.dashboardRead': 'อ่านตัวเลขสรุปในหน้าภาพรวม',
    'apiKeys.scope.aiInvoke': 'สั่งงาน AI กับข้อความที่เก็บไว้',

    'apiKeys.created.title': 'API key ใหม่ของคุณ',
    'apiKeys.created.description': 'คีย์สำหรับ {name}',
    'apiKeys.created.warning':
      'คัดลอกเก็บไว้ตอนนี้ ระบบเก็บเพียงค่า SHA-256 ของคีย์เท่านั้น จึงไม่สามารถแสดงหรือกู้คืนได้อีก หากทำหาย ต้องเพิกถอนแล้วสร้างใหม่',
    'apiKeys.created.copy': 'คัดลอก API key',
    'apiKeys.created.tryIt': 'ลองใช้งาน',
    'apiKeys.created.done': 'คัดลอกเรียบร้อยแล้ว',

    'apiKeys.requestFailed': 'ดำเนินการตามคำขอไม่สำเร็จ กรุณาตรวจสอบว่าเชื่อมต่อเกตเวย์ได้',
  },
});
